import { counterResetStyle, isMarkerFormat, ORDERED_LIST_ATTRS } from '@root/helpers/editor';
import { mergeAttributes } from '@tiptap/core';
import OrderedListExtension from '@tiptap/extension-ordered-list';
import type { Node, NodeType } from '@tiptap/pm/model';
import type { Selection } from '@tiptap/pm/state';
import type { CommandProps } from '@tiptap/react';

import { findFirstNodeAbove } from './helpers';

export type IconType = (typeof iconList)[number];
export const iconList = ['1', 'i', 'a', '1.1'] as const;

const iconType = (index: number): number => {
  const size = iconList.length;
  // We are not traversing 1.1 so we use (size - 1) here.
  return index % (size - 1);
};

const nextIconType = (icon: string): string => {
  switch (icon) {
    case '1.1':
      return '1.1';
    case 'a':
      return 'a';
    case 'i':
      return 'i';
    case '1':
    default:
      return '1';
  }
};

const getIndent = (element: HTMLElement): number => {
  if (element.tagName === 'BODY') return 0;
  const tagName = element.tagName;
  const result = tagName === 'LI' ? 1 : 0;
  const parent = element.parentElement;
  return parent ? result + getIndent(parent) : result;
};

const hasCounterReset = (style: string | null | undefined) => /counter-reset/.test(style ?? '');

export const OrderedList = OrderedListExtension.extend({
  addAttributes() {
    const parent = this.parent ? this.parent() : {};
    return {
      ...parent,
      start: {
        default: 1,
        parseHTML: (element: HTMLElement) => {
          const counterReset = /counter-reset:\s*item\s+(-?\d+)/.exec(element.getAttribute('style') ?? '');
          return counterReset ? Number(counterReset[1]) + 1 : 1;
        },
        renderHTML: ({ start, legacyStart }: { start: number; legacyStart: number | null }) => ({
          [ORDERED_LIST_ATTRS.start]: start !== 1 ? start : (legacyStart ?? 1),
        }),
      },
      // saved without a counter reset, which the stylesheet numbers from 1 whatever this says
      legacyStart: {
        default: null,
        parseHTML: (element: HTMLElement) => {
          if (hasCounterReset(element.getAttribute('style')) || !element.hasAttribute('start')) return null;
          const start = parseInt(element.getAttribute('start') ?? '', 10);
          return start === 1 ? null : start;
        },
        renderHTML: () => ({}),
      },
      [ORDERED_LIST_ATTRS.depth]: {
        default: 0,
        parseHTML: (element: any) => {
          const parent = element.parentElement;
          const result = parent ? getIndent(parent) : 0;
          return result;
        },
      },
      // this is a type that is being set by the recalculate function
      // and is the "final" type that we see in the editors-map
      [ORDERED_LIST_ATTRS.type]: {
        default: '1',
        parseHTML: (element: any) => {
          const result = element.hasAttribute(ORDERED_LIST_ATTRS.type)
            ? element.getAttribute(ORDERED_LIST_ATTRS.type)
            : '1';
          return result;
        },
      },
      // this is a pre set type that is being used in the recalculate function
      // to know to ignore the depth we are being in
      [ORDERED_LIST_ATTRS.presetType]: {
        default: undefined,
        parseHTML: (element: any) => {
          return element.getAttribute(ORDERED_LIST_ATTRS.type) ?? undefined;
        },
      },
      markerFormat: {
        default: undefined,
        parseHTML: (element: HTMLElement) => {
          const markerFormat = element.getAttribute(ORDERED_LIST_ATTRS.markerFormat);
          return isMarkerFormat(markerFormat) ? markerFormat : undefined;
        },
        renderHTML: ({ markerFormat }: { markerFormat?: string }) =>
          markerFormat ? { [ORDERED_LIST_ATTRS.markerFormat]: markerFormat } : {},
      },
      textOffset: {
        default: undefined,
        parseHTML: (element: HTMLElement) => {
          const value = Number(element.getAttribute(ORDERED_LIST_ATTRS.textOffset));
          return element.hasAttribute(ORDERED_LIST_ATTRS.textOffset) && Number.isFinite(value) ? value : undefined;
        },
        renderHTML: ({ textOffset }: { textOffset?: number }) =>
          textOffset === undefined
            ? {}
            : { [ORDERED_LIST_ATTRS.textOffset]: textOffset, style: `--text-offset: ${textOffset}` },
      },
      style: {
        default: undefined,
        parseHTML: (element: HTMLElement) => {
          const style = element.getAttribute('style');
          if (!style) return style ?? undefined;
          return (
            style
              .split(';')
              .filter((declaration) => !declaration.trim().startsWith('--text-offset'))
              .join(';')
              .trim() || undefined
          );
        },
        renderHTML: ({ style, start }: { style?: string; start: number }) =>
          mergeAttributes(
            style === undefined ? {} : { style },
            start !== 1 || hasCounterReset(style) ? { style: counterResetStyle(start) } : {},
          ),
      },
    };
  },

  addCommands() {
    const parent = this.parent ? this.parent() : {};
    return {
      ...parent,
      indentOrderedListRight: () => (args: CommandProps) => {
        args.chain().focus().sinkListItem('listItem').command(recalculate()).focus(args.state.selection.from).run();
        return false;
      },
      indentOrderedListLeft: () => (args: CommandProps) => {
        const node = findFirstNodeAbove(this.name, args.state.selection);
        if (!node) return false;

        if (node?.node?.attrs?.[ORDERED_LIST_ATTRS.depth] > 0) {
          args.chain().liftListItem('listItem').command(recalculate()).run();
        }

        args.commands.focus(args.state.selection.from);
        return false;
      },
      setIconType: (icon: IconType) => (args: CommandProps) => {
        const node = findFirstNodeAbove(this.name, args.state.selection);
        if (!node) return false;
        args.tr.setNodeMarkup(node.before, node.node.type, {
          ...node.node.attrs,
          [ORDERED_LIST_ATTRS.presetType]: icon,
          [ORDERED_LIST_ATTRS.type]: icon,
          markerFormat: undefined,
        });
        return true;
      },
    };
  },
});

// this function recalculates types that the tree should have
const recalculate =
  () =>
  (args: CommandProps): boolean => {
    const root = findRoot(args.state.selection);

    if (!root) return false;

    const tree = buildTree(root.root, root.before);
    decorate(tree, 0, undefined, args);
    args?.dispatch?.();
    return true;
  };

type FoundRoot = { root: Node & { type: NodeType & { name: 'orderedList' } }; before: number };

const findRoot = ({ $from }: Selection): FoundRoot | null => {
  let result: undefined | ReturnType<typeof findRoot>;
  for (let depth = $from.depth; depth >= 0; depth--) {
    const node = $from.node(depth);
    if (node.type.name === 'orderedList') {
      result = { root: node, before: $from.before(depth) } as FoundRoot;
    }
  }

  if (!result) return null;

  return result;
};

// assuming that root is an orderedList
const buildTree = (root: Node & { type: { name: 'orderedList' } }, start: number): Tree => {
  const result = {
    from: start,
    to: start + root.content.size,
    children: [],
    attrs: root.attrs,
  };

  // assuming that descendants are done from top to bottom
  root.descendants((node, pos) => {
    if (node.type.name === 'orderedList') {
      const rpos = root.resolve(pos);
      const start1 = start + pos + (rpos.start() - rpos.before()); // start + offset from parent + size of the token start
      const end = start1 + node.content.size; // node.content.size for the size of the content itself
      const child = {
        from: start1,
        to: end,
        children: [],
        attrs: node.attrs,
      };
      insertTree(result, child);
    }
  });
  return result;
};

const insertTree = (root: Tree, child: Tree): boolean => {
  // assuming that trees form correct brackets expression
  // ie there is no ([)]
  if (root.from < child.from && root.to > child.from) {
    let inserted = false;
    root.children.map((ch) => {
      if (!inserted) {
        inserted = insertTree(ch, child);
      }
    });
    if (!inserted) {
      root.children.push(child);
    }
    return true;
  }
  return false;
};

type Tree = {
  from: number;
  to: number;
  children: Tree[];
  attrs: Node['attrs'];
};

// sets type for a given tree
// the difference between this and updateAttributes is
// that this function sets a type only for the beginning of the selection
// not for all of the nodes in that selection
const setType = (tree: Tree, depth: number, icon: string, args: CommandProps) => {
  args.tr.setNodeMarkup(tree.from, undefined, {
    ...tree.attrs,
    [ORDERED_LIST_ATTRS.type]: tree.attrs[ORDERED_LIST_ATTRS.presetType] ?? icon,
    [ORDERED_LIST_ATTRS.depth]: depth,
  });
};

export const decorate = (tree: Tree, depth = 0, icon: string | undefined, args: CommandProps) => {
  const current = tree.attrs[ORDERED_LIST_ATTRS.presetType] ?? icon ?? iconList[iconType(depth)];
  setType(tree, depth, current, args);
  tree.children.forEach((x) => decorate(x, depth + 1, nextIconType(current), args));
};
